import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Send, Volume2, Square, Share2, RotateCcw, Mic, Type, Heart, Trophy, Bookmark, Swords } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { usePlayer } from "@/context/PlayerContext";
import { Header } from "@/components/Header";
import { PlayerBar } from "@/components/PlayerBar";
import { SimpleRecorder } from "@/components/SimpleRecorder";
import { BAILA_CHALLENGES } from "@/data/bailaChallenges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO = "https://static.prod-images.emergentagent.com/jobs/0e3d4d61-ed21-4f86-b551-f282d91748f8/images/524560ccf7294d1af7142599053d3c65ffae24e2226af6167249403197c8b5b2.jpeg";

const TTS_LANG = { si: "si-LK", en: "en-US", ta: "ta-IN", hi: "hi-IN", te: "te-IN", bn: "bn-IN" };
const TONES = [
  { k: "classic", labelKey: "wb_tone_classic" },
  { k: "funny", labelKey: "wb_tone_funny" },
  { k: "cheeky", labelKey: "wb_tone_cheeky" },
  { k: "romantic", labelKey: "wb_tone_romantic" },
  { k: "rap", labelKey: "wb_tone_rap" },
];

function dayOfYear() {
  const now = new Date();
  return Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000);
}

export default function WadaBaila() {
  const { t, lang, fontClass } = useLang();
  const { current } = usePlayer();
  const [view, setView] = useState("play"); // play | hall
  const [mode, setMode] = useState("ai");
  const [tone, setTone] = useState("classic");
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [player, setPlayer] = useState("p1");
  const [inputKind, setInputKind] = useState("type");
  const [blob, setBlob] = useState(null);
  const [speaking, setSpeaking] = useState(null);
  const [stats, setStats] = useState({ duels: 0, likes: 0 });
  const [liked, setLiked] = useState(false);
  const [savedHall, setSavedHall] = useState(false);
  const [hall, setHall] = useState([]);
  const [hallLoading, setHallLoading] = useState(false);
  const [hallLiked, setHallLiked] = useState({});
  const [challengeFrom, setChallengeFrom] = useState("");
  const [repliedToChallenge, setRepliedToChallenge] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const challenge = (BAILA_CHALLENGES[lang] || BAILA_CHALLENGES.en);
  const todaysChallenge = challenge[dayOfYear() % challenge.length];

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread, loading]);
  useEffect(() => () => { try { window.speechSynthesis.cancel(); } catch (e) {} }, []);
  useEffect(() => { axios.get(`${API}/baila/stats`).then((r) => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const cv = p.get("challenge");
    if (cv) {
      setChallengeFrom(p.get("from") || t("wb_someone"));
      setThread([{ side: "challenger", text: cv }]);
      setView("play");
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, []); // eslint-disable-line
  useEffect(() => {
    if (view !== "hall") return;
    setHallLoading(true);
    axios.get(`${API}/baila/hall`).then((r) => setHall(r.data)).catch(() => {}).finally(() => setHallLoading(false));
  }, [view]);

  const sideLabel = (side) => (side === "you" ? t("wb_you") : side === "app" ? t("wb_app") : side === "challenger" ? (challengeFrom || t("wb_someone")) : side === "p1" ? t("wb_p1") : t("wb_p2"));
  const isRight = (side) => side === "you" || side === "p1";
  const roundNo = thread.filter((v) => ["you", "p1", "p2"].includes(v.side)).length;

  const reset = () => {
    try { window.speechSynthesis.cancel(); } catch (e) {}
    setThread([]); setText(""); setBlob(null); setPlayer("p1"); setInputKind("type"); setSpeaking(null); setLiked(false); setSavedHall(false); setChallengeFrom(""); setRepliedToChallenge(false);
  };
  const switchMode = (m) => { if (m !== mode) { setMode(m); reset(); } };

  const countDuel = () => {
    setStats((s) => ({ ...s, duels: s.duels + 1 }));
    axios.post(`${API}/baila/count`).then((r) => setStats(r.data)).catch(() => {});
  };
  const likeDuel = () => {
    if (liked) return;
    setLiked(true);
    setStats((s) => ({ ...s, likes: s.likes + 1 }));
    axios.post(`${API}/baila/like`).then((r) => setStats(r.data)).catch(() => {});
  };

  const readAloud = (idx, verse) => {
    if (!("speechSynthesis" in window)) { toast.error("Read aloud is not supported on this device."); return; }
    if (speaking === idx) { window.speechSynthesis.cancel(); setSpeaking(null); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(verse);
    u.lang = TTS_LANG[lang] || "en-US";
    u.rate = 0.92;
    const voices = window.speechSynthesis.getVoices();
    const base = (TTS_LANG[lang] || "en").split("-")[0];
    const match = voices.find((v) => v.lang === u.lang) || voices.find((v) => v.lang && v.lang.startsWith(base));
    if (match) u.voice = match;
    u.onend = () => setSpeaking(null);
    u.onerror = () => setSpeaking(null);
    setSpeaking(idx);
    window.speechSynthesis.speak(u);
  };

  const shareVerse = (verse) => {
    const msg = `${verse}\n\n— Neox · ${t("wb_nav")}\n${window.location.origin}/rooms/wada-baila`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const shareWholeDuel = (verses = thread) => {
    const lines = verses.filter((v) => v.text).map((v) => `${sideLabel(v.side)}: ${v.text}`).join("\n\n");
    const msg = `🎵 ${t("wb_nav")}\n\n${lines}\n\n— Neox\n${window.location.origin}/rooms/wada-baila`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const startChallenge = () => {
    setMode("ai"); reset();
    setThread([{ side: "app", text: todaysChallenge }]);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const sendAi = async () => {
    const verse = text.trim();
    if (!verse) return;
    setThread((th) => [...th, { side: "you", text: verse }]);
    setText(""); countDuel(); setSavedHall(false); setLoading(true);
    try {
      const r = await axios.post(`${API}/baila/reply`, { verse, lang, tone });
      setThread((th) => [...th, { side: "app", text: r.data.reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wb_error"));
    } finally { setLoading(false); }
  };
  const sendHuman = () => {
    if (inputKind === "sing") {
      if (!blob) return;
      setThread((th) => [...th, { side: player, audioUrl: URL.createObjectURL(blob) }]);
    } else {
      const verse = text.trim();
      if (!verse) return;
      setThread((th) => [...th, { side: player, text: verse }]);
    }
    setText(""); setBlob(null); countDuel(); setSavedHall(false);
    setPlayer((p) => (p === "p1" ? "p2" : "p1"));
  };
  const onSend = () => {
    if (challengeActive && !repliedToChallenge) return sendChallengeReply();
    return mode === "ai" ? sendAi() : sendHuman();
  };

  const challengeActive = !!challengeFrom;
  const sendChallengeReply = () => {
    const verse = text.trim();
    if (!verse) return;
    setThread((th) => [...th, { side: "you", text: verse }]);
    setText(""); countDuel(); setRepliedToChallenge(true); setSavedHall(false);
  };
  const askName = () => {
    let name = localStorage.getItem("wb_name") || "";
    if (!name) { name = (window.prompt(t("wb_ask_name")) || "").trim() || t("wb_someone"); localStorage.setItem("wb_name", name); }
    return name;
  };
  const shareChallenge = (verse) => {
    if (!verse) { toast.error(t("wb_input_hint")); return; }
    const name = askName();
    const link = `${window.location.origin}/rooms/wada-baila?challenge=${encodeURIComponent(verse)}&from=${encodeURIComponent(name)}&lang=${lang}`;
    const msg = `${name} ${t("wb_challenge_msg")}\n\n"${verse}"\n\n${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const challengeFamily = () => {
    const verse = text.trim() || [...thread].reverse().find((v) => ["you", "p1", "p2"].includes(v.side) && v.text)?.text;
    shareChallenge(verse);
  };
  const sendReplyBack = () => {
    const myVerse = [...thread].reverse().find((v) => v.side === "you" && v.text)?.text;
    shareChallenge(myVerse);
  };

  const saveToHall = async () => {
    const verses = thread.filter((v) => v.text).map((v) => ({ side: v.side, text: v.text }));
    if (verses.length < 2) return;
    try {
      await axios.post(`${API}/baila/hall`, { verses, lang, tone });
      setSavedHall(true);
      toast.success(t("wb_saved_hall"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wb_error"));
    }
  };
  const likeHall = (id) => {
    if (hallLiked[id]) return;
    setHallLiked((m) => ({ ...m, [id]: true }));
    setHall((list) => list.map((d) => (d.id === id ? { ...d, likes: (d.likes || 0) + 1 } : d)));
    axios.post(`${API}/baila/hall/${id}/like`).catch(() => {});
  };

  const canSend = mode === "ai" ? text.trim() && !loading : inputKind === "sing" ? !!blob : !!text.trim();
  const hasTextDuel = thread.filter((v) => v.text).length >= 2;

  return (
    <div className="min-h-screen pb-40" style={{ background: "var(--bg)" }} data-testid="wada-baila-room">
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, color-mix(in srgb, var(--bg) 40%, transparent) 0%, color-mix(in srgb, var(--bg) 78%, transparent) 55%, var(--bg) 100%)" }} />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-14 pb-10 sm:pt-20 sm:pb-14">
          <div className="max-w-2xl sk-fade-up">
            <span className="inline-flex items-center gap-2 font-ui text-xs sm:text-sm font-bold tracking-widest uppercase px-3 py-1.5 rounded-full mb-5" style={{ background: "var(--brass-light)", color: "var(--accent)" }}>
              <Sparkles size={16} /> {t("quiet_note")}
            </span>
            <h1 className={`${fontClass()} text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight`} style={{ color: "var(--text)" }}>
              {t("wb_title")}
            </h1>
            <p className="font-ui text-base sm:text-lg leading-relaxed mt-4" style={{ color: "var(--text-2)" }}>{t("wb_sub")}</p>
            <div className="flex items-center gap-4 mt-5">
              <span className="inline-flex items-center gap-2 font-ui text-sm font-bold px-3.5 py-2 rounded-full" style={{ background: "var(--card)", border: "1px solid var(--border)", color: "var(--text)" }} data-testid="wb-duel-counter">
                <Sparkles size={15} style={{ color: "var(--accent)" }} /> {stats.duels.toLocaleString()} {t("wb_duels_played")}
              </span>
              <span className="inline-flex items-center gap-1.5 font-ui text-sm font-bold px-3.5 py-2 rounded-full" style={{ background: "var(--card)", border: "1px solid var(--border)", color: "var(--text)" }} data-testid="wb-like-counter">
                <Heart size={15} style={{ color: "#e11d48" }} fill="#e11d48" /> {stats.likes.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* View tabs */}
        <div className="flex items-center gap-2 p-1.5 rounded-2xl mb-5" style={{ background: "var(--card)", border: "1px solid var(--border)" }}>
          <button data-testid="wb-view-play" onClick={() => setView("play")} className="flex-1 h-12 rounded-xl font-ui font-bold text-base inline-flex items-center justify-center gap-2 transition-colors"
            style={view === "play" ? { background: "var(--accent)", color: "#fff" } : { background: "transparent", color: "var(--text-2)" }}>
            <Swords size={18} /> {t("wb_play")}
          </button>
          <button data-testid="wb-view-hall" onClick={() => setView("hall")} className="flex-1 h-12 rounded-xl font-ui font-bold text-base inline-flex items-center justify-center gap-2 transition-colors"
            style={view === "hall" ? { background: "var(--accent)", color: "#fff" } : { background: "transparent", color: "var(--text-2)" }}>
            <Trophy size={18} /> {t("wb_hall")}
          </button>
        </div>

        {view === "play" ? (
          <>
            {challengeActive && (
              <div className="rounded-2xl p-4 mb-4 flex items-start gap-3" style={{ background: "var(--brass-light)", border: "1px solid var(--accent)" }} data-testid="wb-challenge-banner">
                <Swords size={20} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }} />
                <p className="font-ui text-sm sm:text-base leading-relaxed" style={{ color: "var(--text)" }}>
                  <b>{challengeFrom}</b> {t("wb_challenged_you")}
                </p>
              </div>
            )}
            {/* Mode toggle */}
            {!challengeActive && (
            <div className="flex items-center gap-2 p-1.5 rounded-2xl mb-4" style={{ background: "var(--card)", border: "1px solid var(--border)" }}>
              {[{ k: "ai", label: t("wb_mode_ai") }, { k: "human", label: t("wb_mode_human") }].map((m) => (
                <button key={m.k} data-testid={`wb-mode-${m.k}`} onClick={() => switchMode(m.k)} className="flex-1 h-11 rounded-xl font-ui font-bold text-base transition-colors"
                  style={mode === m.k ? { background: "var(--accent)", color: "#fff" } : { background: "transparent", color: "var(--text-2)" }}>
                  {m.label}
                </button>
              ))}
            </div>
            )}

            {/* Tone selector (AI mode) */}
            {!challengeActive && mode === "ai" && (
              <div className="mb-4">
                <p className="font-ui text-xs font-bold uppercase tracking-wide mb-2" style={{ color: "var(--text-muted)" }}>{t("wb_tone")}</p>
                <div className="flex flex-wrap gap-2">
                  {TONES.map((to) => (
                    <button key={to.k} data-testid={`wb-tone-${to.k}`} onClick={() => setTone(to.k)} className="h-9 px-3.5 rounded-full font-ui font-semibold text-sm transition-colors"
                      style={tone === to.k ? { background: "var(--accent)", color: "#fff" } : { background: "var(--card)", color: "var(--text-2)", border: "1px solid var(--border)" }}>
                      {t(to.labelKey)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Daily challenge (AI mode, before starting) */}
            {!challengeActive && mode === "ai" && thread.length === 0 && (
              <div className="rounded-2xl p-4 mb-4 flex items-start gap-3" style={{ background: "var(--brass-light)", border: "1px solid var(--border)" }} data-testid="wb-challenge">
                <Sparkles size={20} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }} />
                <div className="flex-1 min-w-0">
                  <p className="font-ui text-xs font-bold uppercase tracking-wide mb-1" style={{ color: "var(--accent)" }}>{t("wb_challenge_title")}</p>
                  <p className={`${fontClass()} text-base leading-relaxed whitespace-pre-line`} style={{ color: "var(--text)" }}>{todaysChallenge}</p>
                  <button data-testid="wb-challenge-reply" onClick={startChallenge} className="mt-3 h-10 px-4 rounded-full font-ui font-bold text-sm" style={{ background: "var(--accent)", color: "#fff" }}>
                    {t("wb_challenge_reply")}
                  </button>
                </div>
              </div>
            )}

            {/* Duel thread */}
            <div className="rounded-3xl p-4 sm:p-5 min-h-[200px]" style={{ background: "var(--card)", border: "1px solid var(--border)" }} data-testid="wb-thread">
              {thread.length === 0 && !loading && (
                <div className="py-12 text-center font-ui text-base sm:text-lg" style={{ color: "var(--text-muted)" }}>{t("wb_empty")}</div>
              )}
              <div className="space-y-4">
                {thread.map((v, i) => (
                  <div key={i} className={`flex ${isRight(v.side) ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[85%] rounded-2xl px-4 py-3" data-testid={`wb-verse-${i}`}
                      style={isRight(v.side) ? { background: "var(--accent)", color: "#fff" } : { background: "var(--bg)", color: "var(--text)", border: "1px solid var(--border)" }}>
                      <p className="font-ui text-xs font-bold opacity-80 mb-1">{sideLabel(v.side)}</p>
                      {v.text && <p className={`${fontClass()} text-lg leading-relaxed whitespace-pre-line`}>{v.text}</p>}
                      {v.audioUrl && <audio controls src={v.audioUrl} className="mt-1 w-56 max-w-full" data-testid={`wb-audio-${i}`} />}
                      {v.text && (
                        <div className="flex items-center gap-3 mt-2 pt-2" style={{ borderTop: `1px solid ${isRight(v.side) ? "rgba(255,255,255,0.25)" : "var(--border)"}` }}>
                          <button data-testid={`wb-read-${i}`} onClick={() => readAloud(i, v.text)} className="inline-flex items-center gap-1.5 font-ui text-xs font-semibold opacity-90 hover:opacity-100">
                            {speaking === i ? <Square size={14} fill="currentColor" /> : <Volume2 size={15} />}
                            {speaking === i ? t("wb_stop_read") : t("wb_read")}
                          </button>
                          <button data-testid={`wb-share-${i}`} onClick={() => shareVerse(v.text)} className="inline-flex items-center gap-1.5 font-ui text-xs font-semibold opacity-90 hover:opacity-100">
                            <Share2 size={15} /> {t("share")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="rounded-2xl px-4 py-3 font-ui text-base italic" style={{ background: "var(--bg)", color: "var(--text-muted)", border: "1px solid var(--border)" }} data-testid="wb-loading">{t("wb_thinking")}</div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
            </div>

            {/* Actions row */}
            {thread.some((v) => v.text) && (
              <div className="flex flex-wrap items-center gap-3 mt-4" data-testid="wb-duel-actions">
                {roundNo > 0 && (
                  <span className="inline-flex items-center gap-1.5 font-ui font-bold text-sm px-3 py-2 rounded-full" style={{ background: "var(--card)", color: "var(--text-2)", border: "1px solid var(--border)" }} data-testid="wb-round">
                    <Swords size={15} /> {t("wb_round")} {roundNo}
                  </span>
                )}
                <button data-testid="wb-like" onClick={likeDuel} disabled={liked} className="inline-flex items-center gap-2 h-11 px-4 rounded-full font-ui font-bold text-sm transition-colors"
                  style={liked ? { background: "#e11d48", color: "#fff" } : { background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}>
                  <Heart size={17} fill={liked ? "#fff" : "none"} /> {liked ? t("wb_liked") : t("wb_like")}
                </button>
                {hasTextDuel && (
                  <button data-testid="wb-save-hall" onClick={saveToHall} disabled={savedHall} className="inline-flex items-center gap-2 h-11 px-4 rounded-full font-ui font-bold text-sm transition-colors"
                    style={savedHall ? { background: "var(--accent)", color: "#fff" } : { background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}>
                    <Bookmark size={17} fill={savedHall ? "#fff" : "none"} /> {savedHall ? t("wb_saved_hall") : t("wb_save_hall")}
                  </button>
                )}
                {hasTextDuel && (
                  <button data-testid="wb-share-duel" onClick={() => shareWholeDuel()} className="inline-flex items-center gap-2 h-11 px-4 rounded-full font-ui font-bold text-sm" style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}>
                    <Share2 size={17} /> {t("wb_share_duel")}
                  </button>
                )}
                {challengeActive && repliedToChallenge && (
                  <button data-testid="wb-send-back" onClick={sendReplyBack} className="inline-flex items-center gap-2 h-11 px-4 rounded-full font-ui font-bold text-sm" style={{ background: "var(--accent)", color: "#fff" }}>
                    <Swords size={17} /> {t("wb_send_back")} {challengeFrom}
                  </button>
                )}
                {!challengeActive && (
                  <button data-testid="wb-challenge-family" onClick={challengeFamily} className="inline-flex items-center gap-2 h-11 px-4 rounded-full font-ui font-bold text-sm" style={{ background: "var(--accent)", color: "#fff" }}>
                    <Swords size={17} /> {t("wb_challenge_family")}
                  </button>
                )}
              </div>
            )}

            {mode === "human" && (
              <p className="mt-4 text-center font-ui text-sm font-bold" style={{ color: "var(--accent)" }} data-testid="wb-turn">
                {t("wb_turn")} {player === "p1" ? t("wb_p1") : t("wb_p2")}
              </p>
            )}

            {/* Input area */}
            <div className="mt-4 rounded-3xl p-4" style={{ background: "var(--card)", border: "1px solid var(--border)" }}>
              {mode === "human" && (
                <div className="flex gap-2 mb-3">
                  <button data-testid="wb-kind-type" onClick={() => { setInputKind("type"); setBlob(null); }} className="flex-1 h-11 rounded-xl font-ui font-semibold text-sm inline-flex items-center justify-center gap-2"
                    style={inputKind === "type" ? { background: "var(--accent)", color: "#fff" } : { background: "var(--bg)", color: "var(--text-2)", border: "1px solid var(--border)" }}>
                    <Type size={16} /> {t("wb_type")}
                  </button>
                  <button data-testid="wb-kind-sing" onClick={() => { setInputKind("sing"); setText(""); }} className="flex-1 h-11 rounded-xl font-ui font-semibold text-sm inline-flex items-center justify-center gap-2"
                    style={inputKind === "sing" ? { background: "var(--accent)", color: "#fff" } : { background: "var(--bg)", color: "var(--text-2)", border: "1px solid var(--border)" }}>
                    <Mic size={16} /> {t("ws_sing")}
                  </button>
                </div>
              )}
              {mode === "human" && inputKind === "sing" ? (
                <SimpleRecorder onRecorded={setBlob} />
              ) : (
                <textarea data-testid="wb-input" ref={inputRef} rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("wb_input_hint")}
                  className={`${fontClass()} w-full px-4 py-3 rounded-xl outline-none resize-y text-lg`} style={{ background: "var(--bg)", color: "var(--text)", border: "1px solid var(--border)" }} />
              )}
              <div className="flex items-center gap-3 mt-3">
                <button data-testid="wb-send" onClick={onSend} disabled={!canSend} className="flex-1 h-14 rounded-xl font-ui font-bold text-lg inline-flex items-center justify-center gap-2 transition-opacity"
                  style={{ background: "var(--accent)", color: "#fff", opacity: canSend ? 1 : 0.5 }}>
                  <Send size={20} /> {t("wb_send")}
                </button>
                {thread.length > 0 && (
                  <button data-testid="wb-new" onClick={reset} className="h-14 px-4 rounded-xl font-ui font-semibold inline-flex items-center gap-2" style={{ background: "var(--bg)", color: "var(--text)", border: "1px solid var(--border)" }}>
                    <RotateCcw size={18} /> <span className="hidden sm:inline">{t("wb_new")}</span>
                  </button>
                )}
              </div>
            </div>
          </>
        ) : (
          /* HALL OF BAILA */
          <div data-testid="wb-hall-view">
            <p className="font-ui text-sm mb-4" style={{ color: "var(--text-2)" }}>{t("wb_hall_sub")}</p>
            {hallLoading && <div className="py-12 text-center font-ui" style={{ color: "var(--text-muted)" }}>{t("loading")}</div>}
            {!hallLoading && hall.length === 0 && (
              <div className="rounded-3xl py-14 text-center font-ui text-base sm:text-lg" style={{ background: "var(--card)", border: "1px solid var(--border)", color: "var(--text-muted)" }} data-testid="wb-hall-empty">
                {t("wb_hall_empty")}
              </div>
            )}
            <div className="space-y-4">
              {hall.map((d, di) => (
                <div key={d.id} className="rounded-3xl p-4 sm:p-5" style={{ background: "var(--card)", border: "1px solid var(--border)" }} data-testid={`wb-hall-duel-${di}`}>
                  {di < 3 && (
                    <span className="inline-flex items-center gap-1.5 font-ui text-xs font-bold px-2.5 py-1 rounded-full mb-3" style={{ background: "var(--brass-light)", color: "var(--accent)" }}>
                      <Trophy size={13} /> #{di + 1}
                    </span>
                  )}
                  <div className="space-y-2.5">
                    {d.verses.map((v, vi) => (
                      <div key={vi} className={`flex ${isRight(v.side) ? "justify-end" : "justify-start"}`}>
                        <div className="max-w-[85%] rounded-2xl px-3.5 py-2.5" style={isRight(v.side) ? { background: "var(--accent)", color: "#fff" } : { background: "var(--bg)", color: "var(--text)", border: "1px solid var(--border)" }}>
                          <p className={`${fontClass()} text-base leading-relaxed whitespace-pre-line`}>{v.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-3 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
                    <button data-testid={`wb-hall-like-${di}`} onClick={() => likeHall(d.id)} disabled={!!hallLiked[d.id]} className="inline-flex items-center gap-2 h-10 px-3.5 rounded-full font-ui font-bold text-sm transition-colors"
                      style={hallLiked[d.id] ? { background: "#e11d48", color: "#fff" } : { background: "var(--bg)", color: "var(--text)", border: "1px solid var(--border)" }}>
                      <Heart size={16} fill={hallLiked[d.id] ? "#fff" : "none"} /> {(d.likes || 0).toLocaleString()}
                    </button>
                    <button data-testid={`wb-hall-share-${di}`} onClick={() => shareWholeDuel(d.verses)} className="inline-flex items-center gap-2 h-10 px-3.5 rounded-full font-ui font-bold text-sm" style={{ background: "var(--bg)", color: "var(--text)", border: "1px solid var(--border)" }}>
                      <Share2 size={16} /> {t("share")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {current && <div style={{ height: 8 }} />}
      <PlayerBar />
    </div>
  );
}
